/**
 * Khung hướng dẫn — mọi câu lệnh harness gửi cho mô hình (khối) và các nguồn
 * dữ liệu nạp vào lượt, dưới dạng danh sách bật/tắt + sửa được, theo bot hoặc
 * cả tổ chức. Không còn gì "ngầm" trong mã: khối nào cũng xem được, tắt được
 * (trừ 6 khối an toàn chỉ sửa chữ), và có nút xem system prompt cuối cùng.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Lock, RotateCcw, Save, Eye, Search, History } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch, Separator } from '@/components/ui/misc'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Loading } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  useKhoiHuongDan, useGhiKhoi, useKhoiPhucKhoi, useXemPromptCuoi, useLichSuKhoi,
  type KhoiHuongDan, type ViTriKhoi,
} from '@/hooks/use-ai'

const NHOM: Array<{ viTri: ViTriKhoi; ten: string; moTa: string }> = [
  { viTri: 'nguon', ten: 'Nguồn nạp vào lượt', moTa: 'Dữ liệu nào được đưa vào prompt. Tắt là không nạp, không có gì ngầm.' },
  { viTri: 'mo_dau', ten: 'Mở đầu', moTa: 'Câu đặt vai trò tổng quát.' },
  { viTri: 'tai_lieu', ten: 'Đi kèm tài liệu', moTa: 'Tiêu đề và cách dùng tiêu chí, kịch bản, ghi chú CRM.' },
  { viTri: 'du_lieu', ten: 'Đi kèm dữ liệu tra cứu', moTa: 'Cách dùng KB, bản đồ danh mục, sản phẩm, tài liệu bán hàng.' },
  { viTri: 'tinh_huong', ten: 'Theo tình huống', moTa: 'Khối chốt đơn, khối chuyển nhân viên — chỉ nạp khi lượt rơi vào tình huống đó.' },
  { viTri: 'cong_cu', ten: 'Công cụ (chế độ agent)', moTa: 'Khi nào gọi search_products / search_knowledge / gửi ảnh / lên đơn.' },
  { viTri: 'cuoi', ten: 'Hướng dẫn cuối & an toàn', moTa: 'Ngôn ngữ, độ dài, chống bịa, chống prompt-injection.' },
  { viTri: 'router', ten: 'Bộ định tuyến (router)', moTa: 'Quyết định trả lời hay chuyển người trước khi sinh câu trả lời.' },
  { viTri: 'critic', ten: 'Kiểm duyệt trước khi gửi (critic)', moTa: 'Lượt kiểm tra thứ hai: cho gửi hay chặn để chuyển người.' },
  { viTri: 'xung_ho', ten: 'Xưng hô', moTa: 'Cách gọi khách / bot tự xưng suy từ tên, giới tính, cách khách tự xưng.' },
  { viTri: 'mo_ta_cong_cu', ten: 'Mô tả công cụ', moTa: 'Đoạn mô tả từng công cụ mà mô hình đọc để quyết định gọi công cụ nào.' },
]

const NHAN_CHE_DO: Record<string, string> = { rag: 'nạp sẵn', agent: 'agent', ca_hai: 'cả hai', router: 'router', critic: 'critic' }

export function KhungHuongDanDialog({ botId, botName, canEdit, open, onClose }: {
  botId: string
  botName: string
  canEdit: boolean
  open: boolean
  onClose: () => void
}) {
  const q = useKhoiHuongDan(botId)
  const ghi = useGhiKhoi()
  const khoiPhuc = useKhoiPhucKhoi()
  const [tab, setTab] = useState<'khoi' | 'xem'>('khoi')
  const [selKey, setSelKey] = useState<string | null>(null)
  const [tuKhoa, setTuKhoa] = useState('')
  const [noiDung, setNoiDung] = useState('')
  /** Ghi ở cấp bot (mặc định) hay cả tổ chức. */
  const [capOrg, setCapOrg] = useState(false)
  const [lichSuMo, setLichSuMo] = useState(false)
  const xem = useXemPromptCuoi(botId, open && tab === 'xem')
  const lichSu = useLichSuKhoi(selKey, capOrg ? null : botId, open && lichSuMo)

  const ds = q.data?.khoi ?? []
  const sel = ds.find((k) => k.key === selKey) ?? null
  useEffect(() => { if (sel) setNoiDung(sel.noiDung) }, [sel?.key, sel?.noiDung]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!selKey && ds.length) setSelKey(ds.find((k) => k.loai === 'khoi')?.key ?? ds[0].key) }, [ds, selKey])

  const theoNhom = useMemo(() => {
    const tk = tuKhoa.trim().toLowerCase()
    const loc = tk ? ds.filter((k) => `${k.ten} ${k.moTa} ${k.key}`.toLowerCase().includes(tk)) : ds
    return NHOM.map((n) => ({ ...n, items: loc.filter((k) => k.viTri === n.viTri).sort((a, b) => a.thuTu - b.thuTu) })).filter((n) => n.items.length)
  }, [ds, tuKhoa])

  const soGhiDe = ds.filter((k) => k.ghiDe).length
  const daSua = sel ? noiDung !== sel.noiDung : false
  const botIdGhi = capOrg ? null : botId

  const batTat = (k: KhoiHuongDan, bat: boolean) =>
    ghi.mutate({ key: k.key, botId: botIdGhi, enabled: bat }, {
      onSuccess: () => toast.success(bat ? `Đã bật "${k.ten}"` : `Đã tắt "${k.ten}"`),
      onError: (e) => toast.error(apiError(e)),
    })

  const luu = () => {
    if (!sel) return
    ghi.mutate({ key: sel.key, botId: botIdGhi, content: noiDung }, {
      onSuccess: () => toast.success(`Đã lưu "${sel.ten}" (${capOrg ? 'cả tổ chức' : 'riêng bot'})`),
      onError: (e) => toast.error(apiError(e)),
    })
  }
  const veMacDinh = () => {
    if (!sel) return
    khoiPhuc.mutate({ key: sel.key, botId: botIdGhi }, {
      onSuccess: () => toast.success('Đã về mặc định'),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="flex h-[88vh] max-w-6xl flex-col gap-3 p-4">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            Khung hướng dẫn · {botName}
            {soGhiDe > 0 && <Badge variant="secondary" className="text-[10px]">{soGhiDe} khối đã tuỳ chỉnh</Badge>}
          </DialogTitle>
          <DialogDescription>
            Mọi câu lệnh hệ thống gửi cho mô hình và mọi nguồn dữ liệu nạp vào lượt. Bật/tắt hoặc sửa từng khối; khối nào cũng khôi phục mặc định được.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'khoi' | 'xem')} className="flex min-h-0 flex-1 flex-col">
          <TabsList className="w-fit shrink-0">
            <TabsTrigger value="khoi">Khối & nguồn ({ds.length})</TabsTrigger>
            <TabsTrigger value="xem"><Eye className="mr-1 h-3.5 w-3.5" /> Xem prompt cuối</TabsTrigger>
          </TabsList>

          <TabsContent value="khoi" className="mt-2 grid min-h-0 flex-1 grid-cols-[19rem_1fr] gap-3">
            {/* Danh sách */}
            <div className="flex min-h-0 flex-col rounded-lg border">
              <div className="relative shrink-0 border-b p-2">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={tuKhoa} onChange={(e) => setTuKhoa(e.target.value)} placeholder="Tìm khối…" className="h-8 pl-8 text-xs" />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                {q.isLoading && <Loading label="Đang tải…" />}
                {theoNhom.map((n) => (
                  <div key={n.viTri} className="mb-2">
                    <p className="px-2 pb-0.5 pt-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground" title={n.moTa}>{n.ten}</p>
                    {n.items.map((k) => (
                      <div
                        key={k.key}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                          selKey === k.key ? 'bg-primary/10 text-primary' : 'hover:bg-accent/60',
                          !k.bat && 'opacity-60',
                        )}
                      >
                        <button type="button" className="min-w-0 flex-1 truncate text-left" onClick={() => setSelKey(k.key)} title={k.moTa}>
                          {k.khoa && <Lock className="mr-1 inline h-3 w-3 text-muted-foreground" />}
                          {k.ten}
                        </button>
                        {k.ghiDe && <span className="shrink-0 text-[9.5px] text-warning">{k.ghiDe === 'bot' ? 'bot' : 'org'}</span>}
                        <Switch
                          checked={k.bat}
                          disabled={!canEdit || k.khoa || ghi.isPending}
                          onCheckedChange={(v) => batTat(k, v)}
                          className="scale-75"
                          title={k.khoa ? 'Khối an toàn — không tắt được' : k.bat ? 'Đang nạp' : 'Đang tắt'}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Trình sửa */}
            <div className="flex min-h-0 flex-col gap-2 rounded-lg border p-3">
              {!sel ? (
                <p className="text-sm text-muted-foreground">Chọn một khối bên trái.</p>
              ) : (
                <>
                  <div className="flex shrink-0 flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-sm font-semibold">
                        {sel.khoa && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                        {sel.ten}
                        {sel.cheDo && <Badge variant="outline" className="text-[10px]">{NHAN_CHE_DO[sel.cheDo] ?? sel.cheDo}</Badge>}
                        {sel.ghiDe && <Badge variant="warning" className="text-[10px]">đã tuỳ chỉnh ({sel.ghiDe === 'bot' ? 'riêng bot' : 'cả tổ chức'})</Badge>}
                        {!sel.bat && <Badge variant="secondary" className="text-[10px]">đang tắt</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground">{sel.moTa}</p>
                      <p className="text-[10.5px] text-muted-foreground">
                        mã <code>{sel.key}</code>
                        {sel.bien.length > 0 && <> · biến: {sel.bien.map((b) => <code key={b} className="mr-1">{`{{${b}}}`}</code>)}</>}
                      </p>
                    </div>
                    {canEdit && (
                      <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        <Switch checked={capOrg} onCheckedChange={setCapOrg} className="scale-75" />
                        Áp cho cả tổ chức
                      </label>
                    )}
                  </div>

                  {sel.loai === 'nguon' ? (
                    <div className="rounded-md bg-muted/40 p-3 text-sm">
                      Nguồn dữ liệu — chỉ bật/tắt, không có nội dung. Đang <b>{sel.bat ? 'nạp' : 'không nạp'}</b> vào prompt.
                    </div>
                  ) : (
                    <>
                      <textarea
                        className="min-h-0 w-full flex-1 resize-none rounded-md border bg-transparent p-3 font-mono text-xs outline-none focus:ring-1 focus:ring-ring"
                        value={noiDung}
                        onChange={(e) => setNoiDung(e.target.value)}
                        disabled={!canEdit}
                        spellCheck={false}
                      />
                      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
                        <p className="text-[11px] text-muted-foreground">
                          {noiDung.length.toLocaleString('vi-VN')} ký tự
                          {sel.khoa && ' · khối an toàn: sửa được chữ, không tắt được'}
                          {noiDung !== sel.macDinh && !daSua && sel.ghiDe && ' · khác mặc định'}
                        </p>
                        <div className="flex items-center gap-1.5">
                          {sel.ghiDe && (
                            <Button size="sm" variant="outline" onClick={veMacDinh} disabled={!canEdit || khoiPhuc.isPending} title="Xoá bản tuỳ chỉnh, dùng lại nội dung mặc định">
                              <RotateCcw className="h-3.5 w-3.5" /> Về mặc định
                            </Button>
                          )}
                          {noiDung !== sel.macDinh && (
                            <Button size="sm" variant="ghost" onClick={() => setNoiDung(sel.macDinh)} disabled={!canEdit}>Dán mặc định vào ô</Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setLichSuMo((v) => !v)} title="Các lần ghi trước của khối này">
                            <History className="h-3.5 w-3.5" /> Lịch sử
                          </Button>
                          <Button size="sm" onClick={luu} disabled={!canEdit || !daSua || ghi.isPending}>
                            <Save className="h-3.5 w-3.5" /> {ghi.isPending ? 'Đang lưu…' : 'Lưu'}
                          </Button>
                        </div>
                      </div>
                      {lichSuMo && (
                        <div className="max-h-40 shrink-0 overflow-y-auto rounded-md border bg-muted/30 p-2 text-[11px]">
                          {lichSu.isLoading && <span className="text-muted-foreground">Đang tải lịch sử…</span>}
                          {lichSu.data && lichSu.data.versions.length === 0 && <span className="text-muted-foreground">Chưa có lần ghi nào (đang dùng mặc định).</span>}
                          {lichSu.data?.versions.map((v) => (
                            <div key={v.id} className="flex items-center gap-2 border-b py-1 last:border-0">
                              <span className="w-28 shrink-0 tabular-nums text-muted-foreground">{new Date(v.createdAt).toLocaleString('vi-VN')}</span>
                              <span className="min-w-0 flex-1 truncate">
                                {v.content == null ? 'mặc định' : `${v.content.length} ký tự`}{v.enabled ? '' : ' · tắt'}
                                {v.updatedBy === 'ai_master' ? ' · AI Master' : ''}{v.ghiChu ? ` · ${v.ghiChu}` : ''}
                              </span>
                              <button
                                type="button" className="shrink-0 text-primary hover:underline" disabled={!canEdit}
                                onClick={() => setNoiDung(v.content ?? sel.macDinh)}
                                title="Dán bản này vào ô soạn (chưa lưu)"
                              >Dán vào ô</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="xem" className="mt-2 min-h-0 flex-1 overflow-hidden">
            {xem.isLoading ? <Loading label="Đang dựng prompt…" /> : xem.data ? (
              <XemPrompt d={xem.data} />
            ) : (
              <p className="text-sm text-destructive">{xem.error ? apiError(xem.error) : 'Không dựng được prompt'}</p>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

function XemPrompt({ d }: { d: { rag: string; agent: string; router: string; ghiChu: string } }) {
  const [che, setChe] = useState<'agent' | 'rag' | 'router'>('agent')
  const noi = d[che]
  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {(['agent', 'rag', 'router'] as const).map((c) => (
          <Button key={c} size="sm" variant={che === c ? 'default' : 'outline'} onClick={() => setChe(c)}>
            {c === 'agent' ? 'Sinh trả lời (agent)' : c === 'rag' ? 'Sinh trả lời (nạp sẵn)' : 'Router'}
          </Button>
        ))}
        <span className="text-[11px] text-muted-foreground">{noi.length.toLocaleString('vi-VN')} ký tự</span>
      </div>
      <p className="shrink-0 rounded-md bg-warning/10 px-3 py-1.5 text-[11px] text-warning">{d.ghiChu}</p>
      <Separator />
      <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 font-mono text-[11px] leading-relaxed">{noi}</pre>
    </div>
  )
}
